import React, { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';

const ImageLightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyPress);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex, onClose, handlePrevious, handleNext]);

  const handleSwipe = (e) => {
    const touchStart = e.touches[0].clientX;
    const handleTouchEnd = (endEvent) => {
      const touchEnd = endEvent.changedTouches[0].clientX;
      if (touchStart - touchEnd > 50) handleNext();
      if (touchEnd - touchStart > 50) handlePrevious();
      document.removeEventListener('touchend', handleTouchEnd);
    };
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        <LucideIcons.X className="w-6 h-6 text-white" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 px-4 py-2 rounded-full">
        <span className="text-white font-semibold">
          {currentIndex + 1} / {images.length}
        </span>
      </div>

      {/* Previous Button */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        >
          <LucideIcons.ChevronLeft className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Next Button */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        >
          <LucideIcons.ChevronRight className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Zoom Toggle */}
      <button
        onClick={() => setIsZoomed(!isZoomed)}
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        {isZoomed ? (
          <LucideIcons.ZoomOut className="w-6 h-6 text-white" />
        ) : (
          <LucideIcons.ZoomIn className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Image */}
      <div
        className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center px-4"
        onTouchStart={handleSwipe}
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className={`max-w-full max-h-full object-contain transition-transform duration-300 ${
            isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          onClick={() => setIsZoomed(!isZoomed)}
        />
      </div>

      {/* Thumbnail Strip */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 overflow-x-auto max-w-[90vw] px-4">
        {images.map((img, idx) => (
          <button
            key={img}
            onClick={() => {
              setCurrentIndex(idx);
              setIsZoomed(false);
            }}
            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
              idx === currentIndex ? 'border-orange-600 scale-110' : 'border-white/30 hover:border-white/60'
            }`}
          >
            <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ImageLightbox;
